(function () {
  'use strict';

  /* A modal may clone the guide together with its data attributes. A WeakSet
     tracks the actual DOM element, so every cloned guide is initialized too. */
  var initializedGuides = new WeakSet();
  var mutationObserver;
  var mattressSpecs = [
    { key: 'single', width: 92, length: 190 },
    { key: 'kingSingle', width: 107, length: 203 },
    { key: 'double', width: 138, length: 190 },
    { key: 'queen', width: 153, length: 203 },
    { key: 'king', width: 183, length: 203 },
    { key: 'superKing', width: 203, length: 203 }
  ];
  var referenceSize = 203;

  function preloadImage(url) {
    return new Promise(function (resolve, reject) {
      var image = new Image();
      image.onload = function () { resolve(url); };
      image.onerror = reject;
      image.src = url;
    });
  }

  function prepareScaleGuide(guide) {
    var assets = window.theme && window.theme.sizeGuideAssets;
    var cards = guide.querySelectorAll('.thl-mattress-card');

    if (!assets || cards.length < mattressSpecs.length) return;

    var urls = mattressSpecs.map(function (spec) { return assets[spec.key]; });
    if (urls.some(function (url) { return !url; })) return;

    var cardParts = mattressSpecs.map(function (spec, index) {
      var card = cards[index];
      return {
        card: card,
        visual: card && card.querySelector('.thl-mattress-visual'),
        frame: card && card.querySelector('.thl-scale-frame'),
        widthLabel: card && card.querySelector('.thl-dimension-width'),
        lengthLabel: card && card.querySelector('.thl-dimension-length'),
        image: card && card.querySelector('.thl-mattress-visual img')
      };
    });

    if (cardParts.some(function (parts) {
      return !parts.card || !parts.visual || !parts.widthLabel || !parts.lengthLabel || !parts.image;
    })) return;

    /* Switch to the proportional layout only after every replacement asset is
       available. The existing CDN artwork therefore remains a complete
       fallback if one of the versioned theme assets fails to load. */
    Promise.all(urls.map(preloadImage)).then(function () {
      mattressSpecs.forEach(function (spec, index) {
        var parts = cardParts[index];
        var frame = parts.frame;
        var widthPercent = (spec.width / referenceSize) * 100;
        var lengthPercent = (spec.length / referenceSize) * 100;

        if (!frame) {
          frame = document.createElement('div');
          frame.className = 'thl-scale-frame';
          parts.visual.appendChild(frame);
        }

        frame.style.setProperty('--thl-width', widthPercent.toFixed(2) + '%');
        frame.style.setProperty('--thl-length', lengthPercent.toFixed(2) + '%');
        frame.style.setProperty('--thl-left', ((100 - widthPercent) / 2).toFixed(2) + '%');
        frame.appendChild(parts.widthLabel);
        frame.appendChild(parts.lengthLabel);
        frame.appendChild(parts.image);

        parts.image.src = urls[index];
        parts.image.width = spec.width;
        parts.image.height = spec.length;
        parts.image.dataset.thlScaleAsset = spec.key;
      });

      var subtitle = guide.querySelector('.thl-mattress-subtitle');
      if (subtitle) {
        subtitle.textContent = 'Shown to scale against Super King (203 × 203 cm)';
      }

      guide.classList.add('thl-scale-ready');
      window.dispatchEvent(new Event('resize'));
    }).catch(function () {
      guide.classList.add('thl-scale-fallback');
    });
  }

  function initGuide(guide) {
    if (initializedGuides.has(guide)) return;
    initializedGuides.add(guide);
    guide.dataset.thlInitialized = 'true';
    guide.classList.add('thl-js-ready');
    prepareScaleGuide(guide);

    var scroller = guide.querySelector('.thl-mattress-list');
    var previous = guide.querySelector('[data-thl-scroll="previous"]');
    var next = guide.querySelector('[data-thl-scroll="next"]');

    function updateControls() {
      if (!scroller || !previous || !next) return;
      var maxScroll = scroller.scrollWidth - scroller.clientWidth;
      previous.disabled = scroller.scrollLeft <= 2;
      next.disabled = scroller.scrollLeft >= maxScroll - 2;
    }

    function scrollCards(direction) {
      var card = scroller && scroller.querySelector('.thl-mattress-card');
      if (!card) return;
      var gap = parseFloat(window.getComputedStyle(scroller).columnGap) || 12;
      scroller.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }

    if (scroller && previous && next) {
      previous.addEventListener('click', function () { scrollCards(-1); });
      next.addEventListener('click', function () { scrollCards(1); });
      scroller.addEventListener('scroll', updateControls, { passive: true });
      window.addEventListener('resize', updateControls, { passive: true });
      updateControls();
      window.requestAnimationFrame(updateControls);

      /* Product popups clone this guide while the modal is still hidden, so
         its first width measurement can be zero. Recheck after the modal's
         opening transition has made the scroller measurable. */
      window.setTimeout(updateControls, 100);
      window.setTimeout(updateControls, 400);
    }

    var revealItems = guide.querySelectorAll('[data-thl-reveal]');
    revealItems.forEach(function (item, index) {
      item.style.setProperty('--thl-delay', Math.min(index * 55, 275) + 'ms');
    });

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12 });
      revealItems.forEach(function (item) { observer.observe(item); });

      /* Never leave product information hidden if a theme modal prevents an
         intersection callback while it is being opened or animated. */
      window.setTimeout(function () {
        revealItems.forEach(function (item) { item.classList.add('is-visible'); });
      }, 1200);
    } else {
      revealItems.forEach(function (item) { item.classList.add('is-visible'); });
    }
  }

  function scanForGuides(root) {
    if (!root || (root.nodeType !== 1 && root.nodeType !== 9)) return;

    if (root.nodeType === 1 && root.matches('#thl-size-guide-pdp')) {
      initGuide(root);
    }

    root.querySelectorAll('#thl-size-guide-pdp').forEach(initGuide);
  }

  function init() {
    scanForGuides(document);

    if (!mutationObserver && 'MutationObserver' in window) {
      mutationObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(scanForGuides);
        });
      });

      mutationObserver.observe(document.body || document.documentElement, {
        childList: true,
        subtree: true
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
